import { useIntl } from 'react-intl';
import { ChevronLeft } from 'lucide-react';
import { foundation } from '../../../ui';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';
import FadeStep from '../../common/FadeStep';
import WizardStepProgress from '../../common/routeOverride/WizardStepProgress';
import OverlayCloseButton, { OVERLAY_CLOSE_TAP_PX } from '../../common/overlay/OverlayCloseButton';

const f = foundation;
const fc = f.color;

// The close button and the back chevron share ONE 44px box at every width (KAN-948: the tap area
// is never smaller than a finger, and the grid never changes between web and touch).
const SIZE = OVERLAY_CLOSE_TAP_PX;
// The panel gutter is 24px; the round close button must sit 16px from the corner. Its 44px box
// holds a 36px circle (4px inside), so the box is pulled out by 24 - 16 + 4 = 12.
const PULL = 12;

function iconButtonStyle(visible, reducedMotion) {
  const size = SIZE;
  return {
    flexShrink: 0,
    width: size,
    height: size,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    borderRadius: f.radii.md,
    color: fc.neutral.textSecondary,
    cursor: visible ? 'pointer' : 'default',
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: reducedMotion ? 'none' : 'opacity 200ms ease',
    padding: 0,
  };
}

/** Read by screen readers, not drawn (the compact header's "Step 1 of 2"). */
const srOnlyStyle = {
  position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
};

/** The close button hides WITHOUT collapsing its slot: opacity 0 (the shared button also goes inert). */
function hiddenFade(visible, reducedMotion) {
  return { opacity: visible ? 1 : 0, transition: reducedMotion ? 'none' : 'opacity 200ms ease' };
}

/**
 * RD-SEC-015 — the MFA stepper's shared header, two variants:
 *  - `'steps'` (setup, change phone): fixed `‹ · label · ✕` slots over
 *    `total` numbered dots (`WizardStepProgress`), patched in place so dots + label tween instead
 *    of the header being rebuilt (spec §4 header contract).
 *  - `'titled'` (turn off, regenerate codes — one-screen modals): title left
 *    + ✕ right, one row, no bars.
 *
 * `canBack`/`canClose` false hides the button WITHOUT collapsing its slot
 * (opacity 0, `pointer-events: none`) — nothing else in the row shifts.
 *
 * `compact` (a phone held sideways, KAN-1012): the `'steps'` dots move UP into
 * the row, between the ‹ and ✕ slots, and the "Step 1 of 2" words stay for
 * screen readers only — the header is one 44px row instead of row + bars, so
 * the code boxes fit the short band between the app header and the menu.
 */
export default function MfaStepperHeader({
  variant = 'steps',
  label,
  current = 0,
  total = 4,
  canBack = false,
  canClose = false,
  onBack,
  onClose,
  title,
  compact = false,
}) {
  const intl = useIntl();
  const reducedMotion = useReducedMotion();
  const size = SIZE;
  const optical = PULL;
  const closeLabel = intl.formatMessage({ id: 'mfaStepper.close', defaultMessage: 'Close' });
  const backLabel = intl.formatMessage({ id: 'twoFactor.back', defaultMessage: 'Back' });

  if (variant === 'titled') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: f.spacePx[3], marginRight: -optical }}>
        <h2
          style={{
            flex: 1,
            margin: 0,
            fontSize: f.fontSize.lg,
            fontWeight: f.fontWeight.bold,
            color: fc.neutral.text,
            fontFamily: f.fontFamily.sans,
            lineHeight: 1.3,
            textAlign: 'left',
            minWidth: 0,
          }}
        >
          {title}
        </h2>
        <OverlayCloseButton
          placement="inline"
          testId="mfa-header-close"
          onClick={onClose}
          disabled={!canClose}
          hidden={!canClose}
          label={closeLabel}
          // Negative top AND bottom: the button's circle centres on the title's first
          // line (18px x 1.3 ~ 23px) and its layout box collapses to that line,
          // so the header -> body gap is the wrapper's 20px, not 20 + the overhang.
          style={{ ...hiddenFade(canClose, reducedMotion), marginTop: Math.round(11.5 - size / 2), marginBottom: Math.round(11.5 - size / 2) }}
        />
      </div>
    );
  }

  // KAN-1182: the app's one step marker (numbered dots), compact = numbers/checks only; the
  // "Step X of Y" words above (or sr-only when `compact`) are the label. Each pill's aria-label
  // is that same string, so no new copy.
  const progressSteps = Array.from({ length: total }, (_, i) => ({
    key: `step-${i + 1}`,
    label: intl.formatMessage({ id: 'mfaStepper.stepOf', defaultMessage: 'Step {a} of {b}' }, { a: i + 1, b: total }),
  }));
  const barRow = (
    <div style={{ marginTop: compact ? 0 : f.spacePx[1] }} role="presentation" data-testid="mfa-header-bars">
      <WizardStepProgress compact flush animated currentStep={current} steps={progressSteps} />
    </div>
  );

  return (
    <div data-testid="mfa-stepper-header">
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${size}px 1fr ${size}px`,
          alignItems: 'center',
          gap: f.spacePx[2],
          margin: `-${optical}px -${optical}px 0`,
        }}
      >
        <button
          type="button"
          data-testid="mfa-header-back"
          onClick={canBack ? onBack : undefined}
          disabled={!canBack}
          aria-label={backLabel}
          aria-hidden={canBack ? undefined : true}
          tabIndex={canBack ? 0 : -1}
          style={iconButtonStyle(canBack, reducedMotion)}
        >
          <ChevronLeft size={20} strokeWidth={2.2} aria-hidden />
        </button>
        {compact ? (
          <div style={{ minWidth: 0 }}>
            <span style={srOnlyStyle}>{label}</span>
            {barRow}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              fontSize: f.fontSize.xs,
              fontWeight: f.fontWeight.bold,
              color: fc.neutral.textMuted,
              letterSpacing: '0.02em',
              overflow: 'hidden',
            }}
          >
            <FadeStep key={label} reduceMotion={reducedMotion}>
              {label}
            </FadeStep>
          </div>
        )}
        <OverlayCloseButton
          placement="inline"
          testId="mfa-header-close"
          onClick={onClose}
          disabled={!canClose}
          hidden={!canClose}
          label={closeLabel}
          style={hiddenFade(canClose, reducedMotion)}
        />
      </div>
      {compact ? null : barRow}
    </div>
  );
}
